import { t } from '../i18n/i18n.js';
import { clear, el, icon } from './dom.js';

/**
 * The Escape menu: a blurred screen and one panel in the middle of it.
 *
 * It replaces three separate pieces of furniture that used to live permanently on top of
 * the race — a floating settings button, a connection pill, and a line of keyboard hints
 * across the bottom of the screen. All three were on-screen a hundred per cent of the time
 * to serve a need that comes up about twice a session, in the corners of a game whose whole
 * appeal is looking out of the windscreen.
 *
 * Deliberately not a pause: this is a multiplayer race, and there is no such thing as
 * stopping the world for one player. The city keeps running behind the blur, which is also
 * why the panel is opaque rather than translucent — a settings list over a moving street
 * scene is unreadable.
 */

export interface PauseMenuOptions {
  /** The settings body. Owned by the caller; this only frames it. */
  content: HTMLElement;
  /** The graphics block, framed the same way. */
  graphics: HTMLElement;
  /** Fires on open and on close, so the caller can start or stop polling. */
  onOpenChange?(open: boolean): void;
}

const CONTROLS: [string, string][] = [
  ['W A S D', t('Drive')],
  [t('Space'), t('Handbrake')],
  ['C', t('Look behind')],
  ['H', t('Horn — the others hear it')],
  ['R', t('Respawn on the route')],
  ['N', t('Whole region / follow the car')],
  ['[  ]', t('Zoom the map')],
  ['G', t('Street map')],
  ['F', t('Chase / aerial camera')],
  ['M', t('Mute')],
  ['Esc', t('This menu')],
];

export class PauseMenu {
  readonly root: HTMLElement;

  private readonly connectionNode: HTMLElement;
  private readonly resumeButton: HTMLButtonElement;
  /** Whatever the session in progress can be ended or left with — see `setActions`. */
  private readonly actions = el('div', { class: 'pause-actions' });
  private isOpenNow = false;

  constructor(private readonly options: PauseMenuOptions) {
    this.connectionNode = el('p', { class: 'pause-connection' }, t('offline'));

    const closeButton = el(
      'button',
      { class: 'pause-close', type: 'button', title: t('Close (Esc)'), id: 'pause-close' },
      icon('close'),
    );
    closeButton.setAttribute('aria-label', t('Close'));
    closeButton.addEventListener('click', () => this.close());

    this.resumeButton = el(
      'button',
      { class: 'button button-primary pause-resume', type: 'button', id: 'pause-resume' },
      t('Close'),
    );
    this.resumeButton.addEventListener('click', () => this.close());

    const controls = el('dl', { class: 'pause-controls' });
    for (const [keys, what] of CONTROLS) {
      controls.append(el('dt', {}, el('kbd', {}, keys)), el('dd', {}, what));
    }

    const scrim = el('div', { class: 'pause-scrim' });
    // Clicking the blur is the other way out, and the one people try first.
    scrim.addEventListener('click', () => this.close());

    this.root = el(
      'div',
      { class: 'pause is-hidden', id: 'pause-menu' },
      scrim,
      el(
        'div',
        { class: 'pause-card', role: 'dialog' },
        el(
          'div',
          { class: 'pause-head' },
          el('h2', { class: 'pause-title' }, 'Menu'),
          closeButton,
        ),
        el(
          'div',
          { class: 'pause-body' },
          section(t('Sound'), this.options.content),
          section(t('Graphics'), this.options.graphics),
          section(t('Controls'), controls),
          section(t('Connection'), this.connectionNode),
        ),
        el('div', { class: 'pause-foot' }, this.actions, this.resumeButton),
      ),
    );
  }

  get isOpen(): boolean {
    return this.isOpenNow;
  }

  /** The connection line the status pill used to carry, now that the pill is gone. */
  setConnection(text: string, ok: boolean): void {
    this.connectionNode.textContent = text;
    this.connectionNode.classList.toggle('is-bad', !ok);
  }

  /** "Back to the race" only means something when there is a race to go back to. */
  setInRace(inRace: boolean, free = false): void {
    this.resumeButton.textContent = !inRace ? t('Close') : free ? t('Back to the drive') : t('Back to the race');
  }

  /**
   * Buttons beside "Back to the race", for a session that is ended from here — a free drive,
   * which has no finish line to end it and no results screen to leave from. Empty otherwise.
   */
  setActions(buttons: HTMLElement[]): void {
    clear(this.actions);
    this.actions.append(...buttons);
  }

  open(): void {
    this.setOpen(true);
  }

  close(): void {
    this.setOpen(false);
  }

  toggle(): void {
    this.setOpen(!this.isOpenNow);
  }

  private setOpen(open: boolean): void {
    if (open === this.isOpenNow) return;
    this.isOpenNow = open;
    this.root.classList.toggle('is-hidden', !open);
    this.options.onOpenChange?.(open);
  }
}

function section(title: string, body: HTMLElement): HTMLElement {
  return el(
    'section',
    { class: 'pause-section' },
    el('h3', { class: 'pause-section-title' }, title),
    body,
  );
}
